import { StatusCode } from "api/protocols";
import { GetCartAudits, ICartAudit } from "api/rpc/2024-04/facilityAdmin/cart/cart";
import axios, { CancelToken } from "axios";
import Drawer from "components/drawerSlide/drawer";
import Spin from "components/spin/spin";
import { errorMessage } from "helpers/Helpers";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import useCurrencyFacility from "hooks/useCurrency/useCurrencyFacility";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { showError } from "redux/actions/ui";
import { ICart } from "redux/reducers/models/cart";
import CartAudits from "./cartAudits/CartAudits";
import ReactDOM from "react-dom";
import { Badge } from "components/badge/Badge";
import useCancelToken from "hooks/useCancelToken/useCancelToken";
import Divider from "components/divider";

interface IProps {
    open: boolean;
    cart: ICart;
    onClose: () => void;
    tableServiceCarts?: Array<ICart>;
}

export default function CartAuditTrailDrawer(props: IProps) {
    const { open, cart, onClose, tableServiceCarts } = props;
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { getCurrencySymbol } = useCurrencyFacility();
    const { cancelToken, cancelRequest } = useCancelToken();
    const { facilityStore } = useAppSelector(store => store);
    const totalLineItems = cart && cart?.line_items ? cart?.line_items?.reduce((total, lineItem) => total + lineItem?.quantity, 0) : 0;
    const [audits, setAudits] = useState<Array<ICartAudit>>(undefined);
    const [staffId, setStaffId] = useState<number>(-1);
    const [staff, setStaff] = useState<Array<{ id: number; full_name: string }>>([{ id: -1, full_name: t("All staff") }]);
    const [activeCartId, setActiveCartId] = useState<number>(null);
    const activeSeatRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const source = axios.CancelToken.source();
        if (open) {
            void getCartAudits(source.token);
        }
        return () => source.cancel();
    }, [open]);

    useEffect(() => {
        if (open && activeSeatRef.current) {
            activeSeatRef.current.scrollIntoView({
                behavior: "smooth",
                inline: "center",
                block: "nearest",
            });
        }
    }, [open, activeCartId]);

    async function getCartAudits(token?: CancelToken) {
        if (tableServiceCarts) {
            setActiveCartId(cart?.id);
        }

        if (audits !== undefined) {
            ReactDOM.unstable_batchedUpdates(() => {
                setStaffId(-1);
                setAudits(undefined);
            });
        }

        const res = await GetCartAudits({ id: cart?.id }, false, token);
        if (res?.status !== StatusCode.OK) {
            if (token && token?.reason) {
                return;
            }
            setAudits([]);
            dispatch(showError(errorMessage(res, t("Error getting cart activity"))));
            return;
        }

        const auditStaff = facilityStore?.staffAccounts?.filter(staff => res?.data?.some(audit => audit?.user_id === staff?.id))?.map(staff => ({ id: staff?.id, full_name: staff?.full_name }));

        ReactDOM?.unstable_batchedUpdates(() => {
            setAudits(res?.data);
            setStaff([{ id: -1, full_name: t("All staff") }, ...auditStaff]);
        });
    }

    async function handleCartSelection(cartId: number) {
        cancelRequest();
        const token = cancelToken();

        // loader
        if (audits !== undefined) {
            ReactDOM.unstable_batchedUpdates(() => {
                setStaffId(-1);
                setAudits(undefined);
                setActiveCartId(cartId);
            });
        }

        const res = await GetCartAudits({ id: cartId }, false, token);
        if (res?.status !== StatusCode.OK) {
            if (token && token?.reason) {
                return;
            }
            setAudits([]);
            dispatch(showError(errorMessage(res, t("Error getting cart activity"))));
            return;
        }
        const auditStaff = facilityStore?.staffAccounts?.filter(staff => res?.data?.some(audit => audit?.user_id === staff?.id))?.map(staff => ({ id: staff?.id, full_name: staff?.full_name }));

        ReactDOM?.unstable_batchedUpdates(() => {
            setAudits(res?.data);
            setStaff([{ id: -1, full_name: t("All staff") }, ...auditStaff]);
        });
    }

    return (
        <Drawer
            open={open}
            title={t("View cart activity")}
            subtitle={`${cart?.admin_user?.full_name ? `${cart?.admin_user?.full_name} •` : ""} ${totalLineItems} ${t("items")} • ${getCurrencySymbol(facilityStore?.facility?.default_currency_code)}${cart?.total_price}`}
            onClose={onClose}
            cancelText={t("Cancel")}
            cancelButtonType="secondary"
            backDropClose
            onOk={onClose}
            okButtonType="secondary"
            okText={t("Close")}
        >
            <Drawer.Section>
                {tableServiceCarts ? (
                    <>
                        <p className="ui-cart-audit-secondary pb-1">{t("Filter by seats")}</p>
                        <div className="flex items-center gap-2 overflow-x-auto ui-cart-audit-scroll-on-hover">
                            {tableServiceCarts?.map((cart, index) => {
                                return (
                                    <div ref={activeCartId === cart.id ? activeSeatRef : null} key={cart.id}>
                                        <Badge onClick={() => handleCartSelection(cart?.id)} className="cursor-pointer" outline size="small" type={activeCartId === cart?.id ? "primary" : "gray"}>{t("Seat")} {index + 1}</Badge>
                                    </div>
                                );
                            })}
                        </div>
                        <Divider small />
                    </>
                ) : null}

                <>
                    <p className="ui-cart-audit-secondary pb-1">{t("Filter by staff")}</p>
                    <div className="flex items-center gap-2 overflow-x-auto">
                        {staff?.map(staff => {
                            return (
                                <Badge key={staff?.id} onClick={() => setStaffId(staff?.id)} className="cursor-pointer" outline size="small" type={staffId === staff?.id ? "primary" : "gray"}>{staff?.full_name}</Badge>
                            );
                        })}
                    </div>
                </>
            </Drawer.Section>
            {!audits ? (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                    <div style={{ width: "60px", height: "60px" }}>
                        <Spin />
                    </div>
                </div>
            ) : <Drawer.Section><CartAudits audits={staffId !== -1 ? audits?.filter(audit => audit?.user_id === staffId) : audits} /></Drawer.Section>}

        </Drawer>
    );
}